import { type FormHTMLAttributes, type HTMLAttributes, type ReactElement, type ReactNode } from 'react';
import { cx } from './library-kit';

/**
 * Small composition pieces the Admin's G-Z screens needed (D551 ADMIN-B). Like the layout vocabulary, none of them accepts a
 * length or a style: every option is a token NAME written as a data attribute (stylesheet: `admin-b.css`).
 */

type PartAttrs = Omit<HTMLAttributes<HTMLElement>, 'style' | 'className' | 'children'>;

// ---- HairlineRow --------------------------------------------------------------------------------------------------------------

export interface HairlineRowProps extends PartAttrs {
  readonly as?: 'div' | 'li';
  /** Cross-axis alignment. Default `center`. */
  readonly align?: 'start' | 'center' | 'baseline';
  readonly children?: ReactNode;
}

/** One hairline-separated record row: name and detail on the left, state and actions on the right (wraps on a narrow screen). */
export function HairlineRow({ as = 'div', align, children, ...rest }: HairlineRowProps): ReactElement {
  const Tag = as;
  return (
    <Tag {...rest} className="vello-ab-itemrow" data-align={align === 'center' ? undefined : align}>
      {children}
    </Tag>
  );
}

/** The growing text side of an {@link HairlineRow}. */
export function HairlineRowMain({ children }: { children?: ReactNode }): ReactElement {
  return <div className="vello-ab-itemrow__main">{children}</div>;
}

// ---- ToneDot --------------------------------------------------------------------------------------------------------------

export type ToneDotTone = 'neutral' | 'success' | 'ink' | 'warning' | 'danger';

/** A small status dot (presence, health). Decorative: pair it with a text label. */
export function ToneDot({ tone = 'neutral' }: { tone?: ToneDotTone }): ReactElement {
  return <span className="vello-ab-dot" data-tone={tone === 'neutral' ? undefined : tone} aria-hidden="true" />;
}

// ---- NoteText -------------------------------------------------------------------------------------------------------------

/**
 * A caption that is a `role="note"` (why a button is missing, what a figure means). `compact` caps it at a line length and aligns
 * it to the end (the note under a right-hand action cell).
 */
export function NoteText({ children, compact = false }: { children?: ReactNode; compact?: boolean }): ReactElement {
  return (
    <span role="note" className="vello-text vello-ab-note" data-role="caption" data-tone="tertiary" data-block="true" data-compact={compact ? 'true' : undefined}>
      {children}
    </span>
  );
}

// ---- BulletList -----------------------------------------------------------------------------------------------------------

/** A short bulleted list inside a notice or a panel (a problem list): indented, tight. */
export function BulletList({ children }: { children?: ReactNode }): ReactElement {
  return <ul className="vello-ab-bullets">{children}</ul>;
}

// ---- FieldGrid ---------------------------------------------------------------------------------------------------

/** A definition list laid out as a responsive grid of `div > dt + dd` pairs (label above value), for a block of read-only facts. */
export function FieldGrid({ children }: { children?: ReactNode }): ReactElement {
  return <dl className="vello-ab-fieldgrid">{children}</dl>;
}

// ---- DrawerForm -----------------------------------------------------------------------------------------------------------

/** A `<form>` that wraps a drawer's header, body and footer: a full-height column so the footer stays pinned and the body scrolls. */
export function DrawerForm({ children, ...rest }: Omit<FormHTMLAttributes<HTMLFormElement>, 'style' | 'className'>): ReactElement {
  return (
    <form {...rest} className="vello-ab-drawerform">
      {children}
    </form>
  );
}

// ---- FieldCell / PreWrap / CodeColumns / TimelineItem / TapLink -------------------------------------------------------------

/** A field (or any block) in a wrapping row that grows from a minimum width: `xs` 180px · `sm` 220px · `md` 260px · `lg` 300px. */
export function FieldCell({ size, children }: { size: 'xs' | 'sm' | 'md' | 'lg'; children?: ReactNode }): ReactElement {
  return (
    <div className="vello-ab-cell" data-size={size}>
      {children}
    </div>
  );
}

/** User-written text that keeps its line breaks and wraps anywhere (a message, a note body). */
export function PreWrap({ children }: { children?: ReactNode }): ReactElement {
  return <div className="vello-ab-prewrap">{children}</div>;
}

/** A list of short codes (recovery codes) in two columns, in the data face. */
export function CodeColumns({ children }: { children?: ReactNode }): ReactElement {
  return <ul className="vello-ab-codecols">{children}</ul>;
}

/** One step of a vertical event trail: a hairline rail on its left, its lines stacked. Put it in a list. */
export function TimelineItem({ children }: { children?: ReactNode }): ReactElement {
  return <li className="vello-ab-tlitem">{children}</li>;
}

/** A text link with a 48px hit box (a link that opens another app, e.g. an authenticator URI). */
export function TapLink({ href, children }: { href: string; children?: ReactNode }): ReactElement {
  return (
    <a href={href} className="vello-ab-taplink">
      {children}
    </a>
  );
}

// ---- CardForm -------------------------------------------------------------------------------------------------------------

/** A `<form>` drawn as a card: a bordered surface whose fields and actions are spaced by one gap (an inline "register / propose" form). */
export function CardForm({ children, ...rest }: Omit<FormHTMLAttributes<HTMLFormElement>, 'style' | 'className'>): ReactElement {
  return (
    <form {...rest} className="vello-card vello-ab-cardform">
      {children}
    </form>
  );
}

// ---- CounterRow / ChatBubble ------------------------------------------------------------------------------------

/** A run of headline counters (`div > dt + dd` pairs): label above a large number, side by side, wrapping. */
export function CounterRow({ children, ...rest }: Omit<HTMLAttributes<HTMLElement>, 'style' | 'className'>): ReactElement {
  return (
    <dl {...rest} className="vello-ab-counters">
      {children}
    </dl>
  );
}

/** One chat message in a thread: `end` is the operator's own side, `start` the other party's. */
export function ChatBubble({ side, children, ...rest }: PartAttrs & { readonly side: 'start' | 'end'; readonly children?: ReactNode }): ReactElement {
  return (
    <li {...rest} className="vello-ab-bubble" data-side={side}>
      {children}
    </li>
  );
}

// ---- CapWidth (the Admin's `Measure` is the stack-based reading width of admin-a) --------------------------------------------------------------------------------------------------------------

export interface CapWidthProps extends PartAttrs {
  /** `xs` 220px · `sm` 320px · `md` 420px · `lg` 560px · `xl` 760px. */
  readonly size: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  readonly className?: string;
  readonly children?: ReactNode;
}

/** Caps a block (a form, a field) at a reading measure. */
export function CapWidth({ size, className, children, ...rest }: CapWidthProps): ReactElement {
  return (
    <div {...rest} className={cx('vello-ab-capwidth', className)} data-size={size}>
      {children}
    </div>
  );
}
